<template>
  <div class="p-4">
    <h1 class="text-2xl font-bold mb-6 text-gray-800">📅 Urlaubskalender</h1>

    <!-- Toolbar -->
    <div class="flex justify-between items-center mb-6">
      <el-button type="primary" icon="el-icon-plus" @click="showCreateModal = true">
        Neuer Eintrag
      </el-button>
    </div>

    <!-- Dialog: Neuer Eintrag -->
    <el-dialog
      title="📝 Neuen Eintrag erstellen"
      :visible.sync="showCreateModal"
      width="500px"
    >
      <el-form :model="newEntry" label-width="120px">
        <el-form-item label="Fahrer">
          <el-select v-model="newEntry.name" placeholder="Bitte wählen...">
            <el-option
              v-for="f in fahrerliste"
              :key="f.name"
              :label="f.name"
              :value="f.name"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Von">
          <el-date-picker v-model="newEntry.from" type="date" style="width: 100%;" />
        </el-form-item>

        <el-form-item label="Bis">
          <el-date-picker v-model="newEntry.to" type="date" style="width: 100%;" />
        </el-form-item>

        <el-form-item label="Kategorie">
          <el-select v-model="newEntry.quelle">
            <el-option label="Urlaub" value="Urlaub" />
            <el-option label="Frei" value="Frei" />
            <el-option label="Krank" value="Krank" />
            <el-option label="Sonstiges" value="Sonstiges" />
          </el-select>
        </el-form-item>

        <el-form-item label="Grund">
          <el-input v-model="newEntry.grund" placeholder="Optionaler Grund" />
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="showCreateModal = false">❌ Abbrechen</el-button>
        <el-button type="primary" @click="saveNewEntry">💾 Speichern</el-button>
      </span>
    </el-dialog>

    <!-- Kalender -->
    <FullCalendar :options="calendarOptions" />

    <!-- Dialog: Event-Details -->
    <el-dialog
      title="📋 Urlaubsdetails"
      :visible.sync="showModal"
      width="500px"
    >
      <div v-if="selectedEvent && selectedEvent.title">
        <p><strong>👤 Name:</strong> {{ selectedEvent.title }}</p>
        <p><strong>📅 Von:</strong> {{ formatDate(selectedEvent.start) }}</p>
        <p><strong>📅 Bis:</strong> {{ formatDate(adjustedEndDate(selectedEvent.end)) }}</p>
        <p><strong>📌 Status:</strong>
          <el-tag
            :type="selectedEvent.status === 'genehmigt' ? 'success'
                  : selectedEvent.status === 'abgelehnt' ? 'danger'
                  : 'warning'">
            {{ selectedEvent.status }}
          </el-tag>
        </p>
        <p><strong>🛈 Quelle:</strong> {{ selectedEvent.quelle }}</p>

        <!-- Fahrer-Zuweisung nur bei WhatsApp -->
        <div v-if="selectedEvent.quelle === 'whatsapp'" class="mt-4">
          <el-form label-width="120px">
            <el-form-item label="Fahrer zuweisen">
              <el-select v-model="selectedEvent.fahrerId" placeholder="Bitte wählen...">
                <el-option
                  v-for="f in fahrerliste"
                  :key="f.name"
                  :label="f.name"
                  :value="f.name"
                />
              </el-select>
            </el-form-item>
            <el-button type="primary" size="small"
              @click="assignDriver(selectedEvent._id, selectedEvent.fahrerId)">
              🚐 Fahrer speichern
            </el-button>
          </el-form>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="closeModal">Schließen</el-button>
        <el-button v-if="selectedEvent.quelle !== 'Frei' && selectedEvent.status !== 'manuell'"
          type="success" @click="handleStatusUpdate('genehmigt')">✅ Genehmigen</el-button>
        <el-button v-if="selectedEvent.quelle !== 'Frei' && selectedEvent.status !== 'manuell'"
          type="danger" @click="handleStatusUpdate('abgelehnt')">❌ Ablehnen</el-button>
        <el-button type="danger" plain @click="deleteEntry(selectedEvent._id)">🗑️ Löschen</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import '@fullcalendar/common/main.css'
import '@fullcalendar/daygrid/main.css'
import interactionPlugin from '@fullcalendar/interaction'
import axios from 'axios'

const API_URL = window.location.hostname === "localhost"
  ? "http://localhost:5000/api"
  : "https://api.flughafentransfer.com/api"

export default {
  components: { FullCalendar },
  data() {
    return {
      showModal: false,
      showCreateModal: false,
      selectedEvent: {},
      fahrerliste: [],
      newEntry: { name: '', from: '', to: '', quelle: 'Urlaub', grund: '' },
      calendarOptions: {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        locale: 'de',
        events: [],
        eventClick: this.handleEventClick
      }
    }
  },
  methods: {
    async handleStatusUpdate(status) {
      try {
        await axios.post(`${API_URL}/urlaub/status`, {
          id: this.selectedEvent._id,
          status,
        })
        this.$message.success(`Status: ${status}`)
        this.closeModal()
        this.loadEvents()
      } catch (err) {
        console.error('❌ Fehler beim Speichern:', err)
        this.$message.error('Fehler beim Aktualisieren')
      }
    },
    async assignDriver(urlaubId, fahrerName) {
      if (!fahrerName) {
        this.$message.error("❌ Bitte zuerst Fahrer auswählen")
        return
      }
      try {
        await axios.post(`${API_URL}/urlaub/assignDriver`, { id: urlaubId, fahrerName })
        this.$message.success('Fahrer erfolgreich zugewiesen ✅')
        this.loadEvents()
      } catch (err) {
        console.error("❌ Fehler bei Fahrer-Zuweisung:", err)
        this.$message.error("Fehler beim Speichern des Fahrers")
      }
    },
    async deleteEntry(id) {
      try {
        await this.$confirm('❌ Soll dieser Eintrag wirklich gelöscht werden?', 'Achtung', {
          confirmButtonText: 'Löschen',
          cancelButtonText: 'Abbrechen',
          type: 'warning'
        })
        await axios.delete(`${API_URL}/urlaub/${id}`)
        this.$message.success('Eintrag gelöscht ✅')
        this.closeModal()
        this.loadEvents()
      } catch (err) {
        if (err !== 'cancel') {
          console.error('❌ Fehler beim Löschen:', err)
          this.$message.error('Fehler beim Löschen')
        }
      }
    },
    async loadEvents() {
      try {
        const res = await axios.get(`${API_URL}/urlaub`)
        this.calendarOptions.events = res.data.map(e => {
          const end = new Date(e.end)
          end.setDate(end.getDate() + 1)
          let farbe = 'orange'
          if (e.status === 'genehmigt') farbe = 'green'
          else if (e.status === 'abgelehnt') farbe = 'red'
          else if (e.status === 'manuell') {
            if (e.quelle === 'Frei') farbe = '#a855f7'
            else if (e.quelle === 'Urlaub') farbe = '#10b981'
            else if (e.quelle === 'Krank') farbe = '#ffffff'
            else farbe = '#f59e0b'
          }
          return {
            id: e.id,
            title: `${e.title || e.name}${e.grund ? ' - ' + e.grund : ''}`,
            start: e.start,
            end: end.toISOString().split("T")[0],
            allDay: true,
            color: farbe,
            extendedProps: {
              status: e.status,
              quelle: e.quelle,
              grund: e.grund,
              phone: e.phone || null,
              _id: e.id
            }
          }
        })
      } catch (err) {
        console.error('❌ Fehler beim Laden:', err)
      }
    },
    async loadFahrer() {
      try {
        const res = await axios.get(`${API_URL}/urlaub/fahrer`)
        this.fahrerliste = res.data.map(f => ({ name: f.name, phone: f.phone }))
      } catch (err) {
        console.error('❌ Fehler beim Laden der Fahrer:', err)
      }
    },
    async saveNewEntry() {
      try {
        await axios.post(`${API_URL}/urlaub`, {
          name: this.newEntry.name,
          from: this.newEntry.from,
          to: this.newEntry.to,
          quelle: this.newEntry.quelle,
          grund: this.newEntry.grund,
          status: 'manuell'
        })
        this.$message.success('Eintrag gespeichert ✅')
        this.showCreateModal = false
        this.newEntry = { name: '', from: '', to: '', quelle: 'Urlaub', grund: '' }
        this.loadEvents()
      } catch (err) {
        console.error('❌ Fehler beim Speichern:', err)
        this.$message.error('Fehler beim Speichern')
      }
    },
    handleEventClick(info) {
      this.selectedEvent = {
        _id: info.event.id,
        title: info.event.title,
        start: info.event.start,
        end: info.event.end,
        status: info.event.extendedProps.status,
        quelle: info.event.extendedProps.quelle,
        phone: info.event.extendedProps.phone || null,
        event: info.event
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.selectedEvent = {}
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('de-DE')
    },
    adjustedEndDate(date) {
      const d = new Date(date)
      d.setDate(d.getDate() - 1)
      return d
    }
  },
  mounted() {
    this.loadEvents()
    this.loadFahrer()
  }
}
</script>
